import { Button, Checkbox, Dialog, TextLink } from '@neo4j-ndl/react';
import { consoleApi } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import React, { useState } from 'react';

import { constructCustomEndpointUrl } from '../../../../../upx/src/custom-endpoints/helpers';
import { PropertyDisplay } from '../../../../../upx/src/instance/property-display';
import { kindOfMigrationFromBool } from './common';

interface Props {
  onAccept: () => void;
  onDecline: () => void;
  liveMigration: boolean;
  instanceId: string;
}

export const ReadBeforeMigrationDialogStep = ({ onAccept, onDecline, liveMigration, instanceId }: Props) => {
  const [confirmed, setConfirmed] = useState(false);
  const { data: customEndpointsSource = [] } = consoleApi.useListCustomEndpointByInstanceQuery(instanceId);
  const hasEndpoint = isNullish(customEndpointsSource) ? false : customEndpointsSource.length > 0;

  const handleConfirmedChange = (checked: boolean) => {
    setConfirmed(checked);
  };
  return (
    <>
      <Dialog.Header>Read before {kindOfMigrationFromBool(liveMigration)} migration</Dialog.Header>
      <Dialog.Description>
        <TextLink type="external" href="https://neo4j.com/docs/aura/tutorials/upgrade/#_perform_the_upgrade">
          Docs
        </TextLink>
      </Dialog.Description>
      <Dialog.Content className="space-y-6">
        <div className="mb-8">
          <ol className="list-decimal pl-6">
            <li className="mb-2">
              Make sure the findings in the Migration Readiness Report have been reviewed and addressed. Once the
              changes have been applied, there should be a period with no detected issues when the report is re-run. As
              workload is analyzed for a maximum of 24 hours, consider whether there is any other workload (i.e. month
              end, year end) which may also need to be checked.
            </li>
            {liveMigration && (
              <li className="mb-2">
                It is highly recommended to complete a test migration before starting the live migration.
              </li>
            )}
            <li className="mb-2">
              By migrating to the latest version, all remaining BTREE indexes without a replacement already created in
              parallel will be automatically converted to RANGE indexes. However, there might be occasions when a
              different index type is more suitable for your application, such as: POINT or TEXT indexes. You can and
              should check your indexes after the migration and drop and recreate indexes as you see fit.{' '}
              <TextLink
                type="external"
                href="https://neo4j.com/docs/upgrade-migration-guide/current/version-5/migration/planning/#_choosing_a_replacement_index"
              >
                Docs
              </TextLink>
            </li>
            <li className="mb-2">
              The migration will <strong>not</strong> include your configurations for:
              <ul className="list-inside list-disc">
                <li>Role-based access control, including users and passwords</li>
                <li>Private link</li>
                <li>Log forwarding on Business Critical instances</li>
                <li>Customer managed encryption keys</li>
              </ul>
              Please make sure to backup or note those configurations, so you can apply them to the test instance after
              the migration.
            </li>
            {liveMigration && (
              <li className="mb-2">
                {hasEndpoint && (
                  <div className="mb-2">
                    Custom endpoint detected on this instance. Please ensure your application(s) are configured to use
                    this so it can be switched to the new instance once migration is complete.{' '}
                    <TextLink type="external" href="https://neo4j.com/docs/aura/managing-databases/custom-endpoints/">
                      Docs
                    </TextLink>
                    {customEndpointsSource.map((ep) => (
                      <PropertyDisplay
                        label="Custom endpoint on this instance"
                        text={constructCustomEndpointUrl(ep)}
                        allowCopyText
                        key={ep.id}
                      />
                    ))}
                  </div>
                )}
                {!hasEndpoint && (
                  <div className="mb-2">
                    This instance does not have a custom endpoint defined for it. We recommend setting up a custom
                    endpoint and configuring your application(s) to use it so that switching to the migrated instance is
                    easier after migration.{' '}
                    <TextLink type="external" href="https://neo4j.com/docs/aura/managing-instances/custom-endpoints/">
                      Docs
                    </TextLink>
                  </div>
                )}
              </li>
            )}
          </ol>
        </div>
        <Checkbox
          label={'I understand it is my responsibility to perform proper testing of my application'}
          onChange={(event) => handleConfirmedChange(event.target.checked)}
          isDisabled={false}
          isChecked={confirmed}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button onClick={onDecline} color="neutral" fill="outlined" size="medium">
          Cancel
        </Button>
        <Button onClick={onAccept} color="primary" fill="filled" size="medium" isDisabled={!confirmed}>
          Acknowledge
        </Button>
      </Dialog.Actions>
    </>
  );
};
